/**
 * Q2b — N4 · Competência 3 (Geometria) · task-model H3.20.K01
 *
 *   "Construir, com régua e compasso, o triângulo simétrico a um triângulo dado em
 *    relação a uma reta."
 *
 * A SEGUNDA METADE DA QUESTÃO 02 do GeoGebra. Na malha (H3.18.K01), refletir é contar
 * quadradinhos; aqui não há malha, e o reflexo precisa ser CONSTRUÍDO. É a mesma
 * transformação, agora como procedimento: a reta r é a mediatriz de cada segmento que
 * liga um vértice à sua imagem.
 *
 * SEM A FERRAMENTA "REFLETIR". Com ela, o item se resolveria em três cliques e deixaria
 * de medir o que interessa — saber que a imagem está na perpendicular, à mesma
 * distância. É a mesma decisão do item da bissetriz, que não oferece a ferramenta
 * "bissetriz".
 *
 * O QUE SE PEDE É O TRIÂNGULO: os três vértices E os três lados. "Construa o triângulo"
 * inclui os lados, e o comando diz isso com todas as letras; a correção e a meta de
 * estrelas seguem o mesmo texto. Antes bastavam os vértices, e quem traçava os lados —
 * fazendo exatamente o que se pedia — perdia estrelas.
 *
 * VÁRIOS CAMINHOS VALEM. O corretor procura, entre os pontos do aluno, a imagem de cada
 * vértice, e depois perturba o triângulo e a reta: quem construiu pela perpendicular,
 * quem usou dois arcos centrados em P e Q, quem misturou os dois — todos passam. Quem
 * largou os pontos a olho, não.
 */

import type { Rng } from '../../core/rng.ts';
import { adjetivo, lista, numeroDe, verbo } from '../../core/pt.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { ConstructionSnapshot, GeoId, GeoObject, GPoint } from '../../geom/construction.ts';
import { addObject, EMPTY_SNAPSHOT, evaluate, geoId } from '../../geom/construction.ts';
import { DEFAULT_SNAP } from '../../geom/snapping.ts';
import type { Tol } from '../../geom/tolerance.ts';
import { charLengthOf } from '../../geom/tolerance.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { dist, toRad } from '../../geom/vec.ts';
import { constructionToScene } from '../../render/geom-scene.ts';
import type { Rect, Shape } from '../../render/scene.ts';
import { boundsOf, scene } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  CONSTR_INCOMPLETO,
  CONSTR_SEM_LADOS,
  SIM_DISTANCIA,
  SIM_EIXO_ERRADO,
  SIM_INCOMPLETO,
  SIM_TRANSLACAO,
} from '../../schema/errors.ts';
import type { ConstructionTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import { ladoTracado, pontosDaConstrucao } from '../_comum/construcao.ts';

const A_ID = geoId('A');
const B_ID = geoId('B');
const C_ID = geoId('C');
const P_ID = geoId('P');
const Q_ID = geoId('Q');
const R_ID = geoId('r');
const VERTICES = [A_ID, B_ID, C_ID] as const;
const NOMES = ['A', 'B', 'C'] as const;

const GIVEN_IDS: ReadonlySet<string> = new Set(['A', 'B', 'C', 'P', 'Q', 'lAB', 'lBC', 'lCA', 'r']);

export interface Params {
  /** Direção do eixo, em graus. */
  readonly inclinacao: number;
  /** Os vértices, em coordenadas do desenho. */
  readonly vertices: readonly [Vec2, Vec2, Vec2];
  readonly P: Vec2;
  readonly Q: Vec2;
}

// ---------------------------------------------------------------------------
// Geometria
// ---------------------------------------------------------------------------

/** Reflexo de X na reta que passa por P e Q. */
export function refletirNaReta(X: Vec2, P: Vec2, Q: Vec2): Vec2 {
  const dx = Q.x - P.x;
  const dy = Q.y - P.y;
  const L2 = dx * dx + dy * dy;
  const t = ((X.x - P.x) * dx + (X.y - P.y) * dy) / L2;
  const F = { x: P.x + t * dx, y: P.y + t * dy };
  return { x: 2 * F.x - X.x, y: 2 * F.y - X.y };
}

/** Lado da reta PQ em que X está (sinal), e distância a ela. */
function ladoDaReta(X: Vec2, P: Vec2, Q: Vec2): number {
  const dx = Q.x - P.x;
  const dy = Q.y - P.y;
  return (dx * (X.y - P.y) - dy * (X.x - P.x)) / Math.hypot(dx, dy);
}

const PERMUTACOES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [0, 2, 1],
  [1, 0, 2],
  [1, 2, 0],
  [2, 0, 1],
  [2, 1, 0],
];

/**
 * O gerador evita direções "fáceis" do eixo — 0°, 45°, 90°… — pelo mesmo motivo do
 * item da bissetriz: com o eixo alinhado à tela, o aluno reflete alinhando pela borda,
 * e o item passa a medir pontaria.
 */
function candidato(g: Rng): Params | null {
  const inclinacao = g.int(0, 179);
  for (const facil of [0, 45, 90, 135, 180]) {
    if (Math.abs(inclinacao - facil) < 12) return null;
  }
  const th = toRad(inclinacao);
  const u = { x: Math.cos(th), y: Math.sin(th) };
  const n = { x: -u.y, y: u.x };
  const sinal = g.bool() ? 1 : -1;
  const vs: Vec2[] = [];
  const ds: number[] = [];
  for (let i = 0; i < 3; i++) {
    const s = g.int(-70, 70);
    const d = g.int(25, 95);
    ds.push(d);
    vs.push({ x: s * u.x + sinal * d * n.x, y: s * u.y + sinal * d * n.y });
  }
  const [a, b, c] = vs as [Vec2, Vec2, Vec2];
  const area2 = Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y));
  if (area2 < 2600) return null;
  if (Math.min(dist(a, b), dist(b, c), dist(c, a)) < 40) return null;
  // Distâncias ao eixo bem diferentes: a mesma abertura de compasso não serve para todos.
  if (Math.max(...ds) - Math.min(...ds) < 20) return null;
  // P e Q sobre o eixo, além da sombra do triângulo: a reta aparece inteira entre eles.
  const tp = g.int(-135, -105);
  const tq = g.int(105, 135);
  return {
    inclinacao,
    vertices: [a, b, c],
    P: { x: tp * u.x, y: tp * u.y },
    Q: { x: tq * u.x, y: tq * u.y },
  };
}

function drawParams(rng: Rng): Params {
  return sampleWhere(rng.fork('geometria'), candidato, (c) => c !== null, {
    label: 'triangulo',
    maxTries: 300,
  }) as Params;
}

function buildGivens(p: Params): ConstructionSnapshot {
  let s = EMPTY_SNAPSHOT;
  p.vertices.forEach((v, i) => {
    s = addObject(s, {
      id: VERTICES[i] as GeoId,
      kind: 'point',
      def: { via: 'given' },
      name: NOMES[i] as string,
      at: v,
    });
  });
  s = addObject(s, {
    id: geoId('lAB'),
    kind: 'line',
    def: { via: 'through', a: A_ID, b: B_ID, span: 'segment' },
  });
  s = addObject(s, {
    id: geoId('lBC'),
    kind: 'line',
    def: { via: 'through', a: B_ID, b: C_ID, span: 'segment' },
  });
  s = addObject(s, {
    id: geoId('lCA'),
    kind: 'line',
    def: { via: 'through', a: C_ID, b: A_ID, span: 'segment' },
  });
  s = addObject(s, { id: P_ID, kind: 'point', def: { via: 'given' }, name: 'P', at: p.P });
  s = addObject(s, { id: Q_ID, kind: 'point', def: { via: 'given' }, name: 'Q', at: p.Q });
  s = addObject(s, {
    id: R_ID,
    kind: 'line',
    def: { via: 'through', a: P_ID, b: Q_ID, span: 'line' },
  });
  return s;
}

function imagens(p: Params): [Vec2, Vec2, Vec2] {
  return p.vertices.map((v) => refletirNaReta(v, p.P, p.Q)) as [Vec2, Vec2, Vec2];
}

/** Enquadramento: triângulo, reflexo e os dois pontos do eixo, com folga. */
function quadro(p: Params): Rect {
  const pts = [...p.vertices, ...imagens(p), p.P, p.Q];
  return boundsOf(
    pts.map((q): Shape => ({ t: 'point', p: q, role: 'given' })),
    0.14,
  );
}

/** O "r" junto da ponta do eixo que fica dentro do quadro. */
function rotuloDoEixo(p: Params): Shape {
  const u = { x: p.Q.x - p.P.x, y: p.Q.y - p.P.y };
  const L = Math.hypot(u.x, u.y);
  const at = { x: p.Q.x + (u.x / L) * 22, y: p.Q.y + (u.y / L) * 22 };
  return { t: 'label', at, text: [txt('r')], anchor: 'ne' };
}

// ---------------------------------------------------------------------------
// Correção
// ---------------------------------------------------------------------------

interface Dados {
  readonly vs: readonly [GPoint, GPoint, GPoint];
  readonly P: GPoint;
  readonly Q: GPoint;
  readonly L: number;
}

function dados(objs: ReadonlyMap<GeoId, GeoObject>): Dados | null {
  const pega = (id: GeoId): GPoint | null => {
    const o = objs.get(id);
    return o && o.kind === 'point' ? o : null;
  };
  const a = pega(A_ID);
  const b = pega(B_ID);
  const c = pega(C_ID);
  const P = pega(P_ID);
  const Q = pega(Q_ID);
  if (!a || !b || !c || !P || !Q) return null;
  return { vs: [a, b, c], P, Q, L: charLengthOf([a.pos, b.pos, c.pos]) };
}

/**
 * Os pontos da construção do aluno: os marcados E os cruzamentos do que ele traçou (ver
 * `_comum/construcao.ts`). As circunferências de centro P e de centro Q por A já dão A′
 * no segundo cruzamento — marcado ou não.
 */
function pontosDoAluno(objs: ReadonlyMap<GeoId, GeoObject>): GPoint[] {
  return pontosDaConstrucao(objs, GIVEN_IDS, dados(objs)?.L ?? 1);
}

/** Para cada vértice, o ponto do aluno que é a sua imagem (ou `null`). */
function achados(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): (GPoint | null)[] {
  const d = dados(objs);
  if (!d) return [null, null, null];
  const ms = pontosDoAluno(objs);
  return d.vs.map((v) => {
    const alvo = refletirNaReta(v.pos, d.P.pos, d.Q.pos);
    let melhor: GPoint | null = null;
    let md = Infinity;
    for (const m of ms) {
      const e = dist(m.pos, alvo);
      if (e < md) {
        md = e;
        melhor = m;
      }
    }
    return melhor && md / d.L <= tol.distRel ? melhor : null;
  });
}

/** Os três lados de A′B′C′ traçados, entre os vértices achados (só faz sentido com os três). */
function ladosTracados(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): boolean {
  const d = dados(objs);
  const ach = achados(objs, tol);
  if (!d || ach.some((x) => x === null)) return false;
  const [a, b, c] = ach.map((x) => (x as GPoint).pos) as [Vec2, Vec2, Vec2];
  const eps = tol.distRel * d.L;
  return (
    ladoTracado(objs, a, b, eps) && ladoTracado(objs, b, c, eps) && ladoTracado(objs, c, a, eps)
  );
}

/** Três pontos do aluno que sejam o triângulo apenas deslocado. */
function transladou(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): boolean {
  const d = dados(objs);
  if (!d) return false;
  const ms = pontosDoAluno(objs);
  const [a, b, c] = d.vs.map((v) => v.pos) as [Vec2, Vec2, Vec2];
  const perto = (x: Vec2): boolean => ms.some((m) => dist(m.pos, x) / d.L <= tol.distRel);
  return ms.some((m) => {
    const v = { x: m.pos.x - a.x, y: m.pos.y - a.y };
    if (Math.hypot(v.x, v.y) / d.L < 0.2) return false;
    return perto({ x: b.x + v.x, y: b.y + v.y }) && perto({ x: c.x + v.x, y: c.y + v.y });
  });
}

/**
 * Três pontos do aluno que sejam o reflexo do triângulo numa reta que NÃO é r.
 *
 * Para cada escolha da imagem de A, a reta candidata é a mediatriz de A e dessa
 * imagem; basta conferir se ela leva B e C em outros dois pontos do aluno.
 */
function refletiuEmOutraReta(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): boolean {
  const d = dados(objs);
  if (!d) return false;
  const ms = pontosDoAluno(objs);
  const [a, b, c] = d.vs.map((v) => v.pos) as [Vec2, Vec2, Vec2];
  const perto = (x: Vec2): boolean => ms.some((m) => dist(m.pos, x) / d.L <= tol.distRel);
  return ms.some((m) => {
    if (dist(m.pos, a) / d.L < 0.1) return false;
    const M = { x: (a.x + m.pos.x) / 2, y: (a.y + m.pos.y) / 2 };
    const w = { x: m.pos.y - a.y, y: -(m.pos.x - a.x) };
    const N = { x: M.x + w.x, y: M.y + w.y };
    // É o próprio r? Então não é "outra reta".
    const mesmaQueR =
      Math.abs(ladoDaReta(M, d.P.pos, d.Q.pos)) / d.L <= tol.distRel &&
      Math.abs(ladoDaReta(N, d.P.pos, d.Q.pos)) / d.L <= tol.distRel * 4;
    if (mesmaQueR) return false;
    return perto(refletirNaReta(b, M, N)) && perto(refletirNaReta(c, M, N));
  });
}

/** Cada vértice tem um ponto do aluno na perpendicular a r, do outro lado — mas não à distância certa. */
function errouDistancia(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): boolean {
  const d = dados(objs);
  if (!d) return false;
  const ms = pontosDoAluno(objs);
  const ach = achados(objs, tol);
  if (ach.every((x) => x !== null)) return false;
  const u = { x: d.Q.pos.x - d.P.pos.x, y: d.Q.pos.y - d.P.pos.y };
  const Lu = Math.hypot(u.x, u.y);
  return d.vs.every((v, i) => {
    if (ach[i]) return true;
    const lv = ladoDaReta(v.pos, d.P.pos, d.Q.pos);
    return ms.some((m) => {
      const lm = ladoDaReta(m.pos, d.P.pos, d.Q.pos);
      if (lm * lv >= 0 || Math.abs(lm) / d.L < 0.05) return false;
      // Na perpendicular: a projeção sobre r coincide com a do vértice.
      const proj = ((m.pos.x - v.pos.x) * u.x + (m.pos.y - v.pos.y) * u.y) / Lu;
      return Math.abs(proj) / d.L <= tol.distRel;
    });
  });
}

const alvo: ConstructionTarget = {
  describe: [
    txt(
      'Construa os pontos A′, B′ e C′, simétricos de A, B e C em relação à reta r, e trace os três lados do triângulo A′B′C′.',
    ),
  ],

  predicate(objs, _g, tol) {
    const ach = achados(objs, tol);
    const n = ach.filter((x) => x !== null).length;
    const ok = n === 3 && ladosTracados(objs, tol);
    return {
      name: 'reflexoConstruido',
      ok,
      residual: 3 - n + (ok || n < 3 ? 0 : 1),
      unit: 'count',
      tolerance: 0,
      message: [
        txt(
          ok
            ? 'O triângulo simétrico foi construído: os três vértices e os três lados.'
            : n === 3
              ? 'Os três vértices simétricos foram construídos, mas faltam lados do triângulo A′B′C′.'
              : `Dos três vértices simétricos, ${construidos(n)}.`,
        ),
      ],
    };
  },

  diagnostics: [
    {
      error: CONSTR_SEM_LADOS,
      test: (objs, _g, tol) =>
        achados(objs, tol).every((x) => x !== null) && !ladosTracados(objs, tol),
    },
    { error: SIM_TRANSLACAO, test: (objs, _g, tol) => transladou(objs, tol) },
    { error: SIM_EIXO_ERRADO, test: (objs, _g, tol) => refletiuEmOutraReta(objs, tol) },
    { error: SIM_DISTANCIA, test: (objs, _g, tol) => errouDistancia(objs, tol) },
    { error: SIM_INCOMPLETO, test: (objs, _g, tol) => achados(objs, tol).some((x) => x !== null) },
    { error: CONSTR_INCOMPLETO, test: () => true },
  ],

  robustnessTrials: 20,

  // Um quarto por vértice e um quarto pelos três lados.
  partialCredit: [
    ...NOMES.map((nome, i) => ({
      id: `imagem-${nome}`,
      label: `Construiu ${nome}′, o simétrico de ${nome}`,
      weight: 1 / 4,
      test: (objs: ReadonlyMap<GeoId, GeoObject>, _g: ReadonlyMap<GeoId, GeoObject>, tol: Tol) =>
        achados(objs, tol)[i] !== null,
    })),
    {
      id: 'lados',
      label: 'Traçou os três lados de A′B′C′',
      weight: 1 / 4,
      test: (objs: ReadonlyMap<GeoId, GeoObject>, _g: ReadonlyMap<GeoId, GeoObject>, tol: Tol) =>
        ladosTracados(objs, tol),
    },
  ],

  analyze(objs, _g, tol) {
    const ach = achados(objs, tol);
    const out: Inline[] = [];
    const feitos = NOMES.filter((_n, i) => ach[i]).map((n) => `${n}′`);
    const aOlho = NOMES.filter((_n, i) => ach[i]?.def.via === 'free').map((n) => `${n}′`);
    if (feitos.length === 0) out.push(txt('Nenhum dos vértices simétricos está no lugar certo.'));
    else if (feitos.length === 3)
      out.push(txt('Os três vértices simétricos — A′, B′ e C′ — estão no lugar certo.'));
    else out.push(txt(`Estão no lugar certo: ${feitos.join(' e ')}.`));
    if (aOlho.length > 0) {
      out.push(
        br,
        txt(
          `${lista(aOlho)} ${verbo('ser.pret', numeroDe(aOlho.length))} ${adjetivo('posicionado', 'm', numeroDe(aOlho.length))} a olho, sem ` +
            'depender do triângulo nem da reta: quando os dados se movem, eles ficam parados.',
        ),
      );
    }
    if (feitos.length === 3 && !ladosTracados(objs, tol)) {
      out.push(
        br,
        txt(
          'Faltam lados: o triângulo A′B′C′ só fica construído com os lados A′B′, B′C′ e C′A′ traçados.',
        ),
      );
    }
    if (transladou(objs, tol)) {
      out.push(
        br,
        txt('Há três pontos que formam o triângulo ABC apenas deslocado: faltou espelhar.'),
      );
    } else if (refletiuEmOutraReta(objs, tol)) {
      out.push(
        br,
        txt(
          'Há três pontos que formam um reflexo do triângulo, mas em relação a outra reta, não a r.',
        ),
      );
    }
    NOMES.forEach((n, i) => {
      if (ach[i]) return;
      out.push(
        br,
        strong(`${n}′: `),
        txt(
          `fica na perpendicular a r que passa por ${n}, do outro lado, à mesma distância de r que ${n}.`,
        ),
      );
    });
    return out;
  },

  reviewShapes(objs, _g, tol) {
    const d = dados(objs);
    if (!d) return [];
    const ims = d.vs.map((v) => refletirNaReta(v.pos, d.P.pos, d.Q.pos));
    const ach = achados(objs, tol);
    const raio = 0.07 * d.L;
    const formas: Shape[] = [{ t: 'poly', pts: ims, closed: true, role: 'construction' }];
    ims.forEach((im, i) => {
      if (ach[i]) formas.push({ t: 'circle', c: im, r: raio, role: 'result' });
      else
        formas.push(
          { t: 'circle', c: im, r: raio, role: 'construction' },
          { t: 'point', p: im, role: 'ghost' },
        );
    });
    return formas;
  },

  reviewLegend: [
    txt(
      'Contorno tracejado: o triângulo simétrico esperado. Anel colorido: vértice construído no lugar certo. ',
    ),
    txt('Anel tracejado: vértice que faltou.'),
  ],
};

// ---------------------------------------------------------------------------

export const simetriaConstrucao: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('h3-20-K01-simetria-construcao'),
  // v2: o triângulo pedido inclui os lados — no comando, na correção e na meta.
  version: 2,
  taskModel: taskModelCode('H3.20.K01'),
  skills: [skillCode('H3.20')],
  axis: 'II',
  level: 'N4',
  title: 'Construir o triângulo simétrico em relação a uma reta',
  // Crédito parcial por vértice construído.
  responseModel: 'GPCM',

  difficultySources: ['construction.free-tools', 'construction.long', 'steps.3plus'],

  params: drawParams,
  solve: buildGivens,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // O triângulo "atravessado" em relação ao eixo — um vértice bem mais longe que os
    // outros — obriga a recomeçar o raciocínio a cada vértice.
    const ds = p.vertices.map((v) => Math.abs(ladoDaReta(v, p.P, p.Q)));
    if (Math.max(...ds) - Math.min(...ds) > 50) fontes.push('construction.no-hint');
    return fontes;
  },

  build(p) {
    const givens = buildGivens(p);
    const vb = quadro(p);
    const desc =
      'Triângulo ABC e, fora dele, a reta r, que passa pelos pontos P e Q, em posição inclinada. ' +
      'O triângulo está inteiramente de um lado da reta. A tarefa é construir o triângulo ' +
      'simétrico ao ABC em relação a r, do outro lado da reta.';
    const base = constructionToScene(evaluate(givens), {
      title: 'Triângulo ABC e reta r',
      desc,
      givenIds: GIVEN_IDS,
      viewBox: vb,
    });
    const cena = scene([...base.shapes, rotuloDoEixo(p)], { a11y: base.a11y, viewBox: vb });
    const L = charLengthOf(p.vertices);

    return {
      stem: [txt('A figura mostra o triângulo ABC e a reta r, que passa pelos pontos P e Q.')],
      comando: [
        txt('Usando as ferramentas disponíveis, construa o triângulo '),
        strong('A′B′C′'),
        txt(', simétrico ao triângulo ABC em relação à reta r: os três '),
        strong('vértices'),
        txt(' e os três '),
        strong('lados'),
        txt('. Um lado pode ser um segmento ou parte de uma reta traçada.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'figura',
          scene: cena,
          alt: desc,
        },
      ],
      interaction: {
        kind: 'construct',
        // SEM 'reflect': é a ausência dela que faz o item medir a reflexão.
        toolbelt: [
          'select',
          'point',
          'segment',
          'line',
          'perpendicular',
          'circle-center-through',
          'compass',
          'intersect',
          'undo',
          'delete',
        ],
        givens,
        backdrop: [rotuloDoEixo(p)],
        viewBox: vb,
        // Move-se o triângulo E a reta: uma construção de verdade acompanha os dois.
        perturbable: [
          { id: A_ID, radius: 0.12 * L },
          { id: B_ID, radius: 0.12 * L },
          { id: C_ID, radius: 0.12 * L },
          { id: P_ID, radius: 0.1 * L },
          { id: Q_ID, radius: 0.1 * L },
        ],
        target: alvo,
        snapping: { ...DEFAULT_SNAP, pixelRadius: 14 },
        maxActions: 80,
        // Por vértice, as circunferências de centro P e de centro Q por ele: o segundo
        // cruzamento delas é a imagem. Três vértices, seis traços; e os três lados.
        meta: 9,
      },
      dicas: [
        [
          txt(
            'Na simetria em relação a r, cada ponto da reta fica à mesma distância de um vértice e da imagem dele.',
          ),
        ],
        [
          txt(
            'P está na reta: a circunferência de centro P que passa por A também passa por A′. O mesmo vale para Q.',
          ),
        ],
        [
          txt(
            'A′ é o segundo ponto em que as circunferências de centro P e de centro Q, ambas passando por A, se cortam. Repita com B e C, e trace os lados.',
          ),
        ],
      ],
      rationale: [
        strong('Uma construção possível, entre várias. '),
        txt(
          '1) Com a circunferência de centro P passando por A, e a de centro Q passando por A, obtêm-se dois arcos que se cortam em A e num segundo ponto: esse é A′. ',
        ),
        txt(
          "Isso funciona porque P e Q ficam, cada um, à mesma distância de A e de A′ — então r é a mediatriz de AA'. ",
        ),
        txt('2) Repita com B e com C, obtendo B′ e C′. 3) Trace os segmentos A′B′, B′C′ e C′A′.'),
        br,
        txt(
          'Outro caminho: trace a perpendicular a r por A, marque o ponto F em que ela corta r, e ' +
            'trace a circunferência de centro F passando por A; ela corta a perpendicular em A′.',
        ),
        br,
        txt(
          'O triângulo simétrico fica "virado": percorrido na ordem A′, B′, C′, ele gira no sentido ' +
            'oposto ao de ABC. Copiá-lo do outro lado sem espelhar dá uma translação, não uma simetria.',
        ),
      ],
      declared: 'dificil',
    };
  },

  validate(_b, p) {
    // O reflexo não pode coincidir com uma translação do triângulo (triângulo simétrico demais).
    const ims = imagens(p);
    const [a, b, c] = p.vertices;
    const L = charLengthOf(p.vertices);
    const coincide = PERMUTACOES.some(([i, j, k]) => {
      const v = { x: (ims[i] as Vec2).x - a.x, y: (ims[i] as Vec2).y - a.y };
      return (
        dist({ x: b.x + v.x, y: b.y + v.y }, ims[j] as Vec2) / L < 0.05 &&
        dist({ x: c.x + v.x, y: c.y + v.y }, ims[k] as Vec2) / L < 0.05
      );
    });
    return coincide ? 'o reflexo coincide com uma translação' : null;
  },
};

/** "nenhum foi construído", "só 1 foi construído", "2 foram construídos" — "nenhum" concorda no singular. */
function construidos(n: number): string {
  const nn = n === 0 ? 'sg' : numeroDe(n);
  const quantos = n === 0 ? 'nenhum' : n === 1 ? 'só 1' : String(n);
  return `${quantos} ${verbo('ser.pret', nn)} ${adjetivo('construido', 'm', nn)}`;
}
